import React from 'react';
import { Reveal } from './Reveal';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';

const faqs = [
  {
    question: "Do I need to already have 'good' eating habits to start?",
    answer: "Not at all. People start this programme at very different points. Some feel confused about food choices. Others eat fairly well but feel inconsistent. Both are equally valid starting points. We start with your reality, not what Instagram says you should be doing. You don't need perfect habits to begin. My role is to meet you where you are and help you build habits gradually, without overwhelm or judgement."
  },
  {
    question: "Can this work if I eat regular home food with my family?",
    answer: "Absolutely. This approach is built around regular food like Rotis, rice, sabzi, dal, curries etc. Many home foods are already nutritious. The work is about adjusting frequency, portions, ingredients and combinations. We increase what supports your health and reduce what doesn't. You won't be asked to eat unfamiliar dishes or follow 'diet food.'"
  },
  {
    question: "What kinds of food changes does this usually involve?",
    answer: "There will be changes. Some foods that are oily, heavily processed will be reduced because they don't support your goals. This is necessary. At the same time, we don't jump to extremes right at the beginning. The focus is on improving everyday meals by adjusting portions, frequency, timing, and balance gradually. You'll get a meal plan in advance so you can plan groceries and meals ahead of time. This structure is intentional. It removes guesswork and makes consistency easier without relying only on willpower."
  },
  {
    question: "Can this work with irregular work hours, eating out, travel, or social events?",
    answer: "Yes, but it requires conscious choices and some planning. Irregular schedules do make consistency harder. We don't expect avoidance of social life. You'll learn how to prioritise on busy days, make better choices with limited options, and get back on track without guilt. Progress comes from awareness and adaptability, so decisions start coming from what your body needs. That's what makes this sustainable in real life."
  },
  {
    question: "How is this different from a generic diet plan or meal chart?",
    answer: "Generic plans hand you a one-size-fits-all chart and expect compliance. Our approach is personalised to your body, lifestyle, habits, and health markers in depth while designing your plan. What truly sets this apart is ongoing guidance. You don't just receive a plan and figure it out alone. You get real-time feedback, regular reviews, and adjustments throughout the programme."
  },
  {
    question: "How do you measure progress beyond just body weight?",
    answer: "Think of your health journey like a puzzle. Weight is just one piece. We also track energy levels, sleep quality, digestion, cravings, mood, strength, measurements, symptoms, and blood parameters where relevant. Many clients notice these improvements well before the scale changes. And often, those changes are the signs that real, lasting progress is happening."
  },
  {
    question: "What if results are slower than expected?",
    answer: "That's completely normal. Every body responds differently, and progress can be influenced by stress, sleep, hormones, routine, and consistency. If things feel slow, we reassess and adjust instead of tightening rules or adding pressure. Slow, steady progress usually lasts far longer than quick fixes and that's what we're aiming for."
  },
  {
    question: "Is this a guaranteed-results programme?",
    answer: "There's no honest way to guarantee a specific outcome because every body is different. What we can promise is that we use personalised, evidence-based methods and put in genuine effort to support you. With consistent effort from your side and strong accountability from us, results do come even if they take time."
  },
  {
    question: "What am I actually paying for beyond a meal plan?",
    answer: "You're paying for strategy, guidance, and accountability, not just a list of foods. Meal plans are tools. The real value lies in understanding your challenges, adjusting the approach when life happens, and helping you stay consistent without guilt or burnout. You're also paying for experienced oversight. Someone who reviews patterns, flags issues early, asks the right questions, and course-corrects before small lapses turn into long breaks. That steady involvement is what keeps progress moving."
  },
  {
    question: "What happens after the programme ends?",
    answer: "The aim is to make you independent. By the end of the programme, you should feel confident making food choices, adjusting portions, handling travel or social events, and getting back on track when things go off-plan. It's about building skills, awareness, and trust in your own decisions so you can support your health long-term."
  }
];

export const FAQSection = () => {
  return (
    <section 
      className="section-padding"
      style={{ background: 'var(--brand-light-lavender)' }}
    >
      <div className="container-custom">
        <div className="max-w-4xl mx-auto">
          <Reveal className="text-center mb-16">
            <h2 className="heading-1 text-balance">Things You Might Be Wondering</h2>
            <p className="body-large mt-6" style={{ color: 'var(--brand-muted-grey)' }}>
              Everything you need to know before starting your journey
            </p>
          </Reveal>

          <Reveal delay={0.1}>
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem 
                key={faq.question} 
                value={`item-${index}`}
                data-testid={`faq-item-${index}`}
                className="px-8 py-4 transition-smooth bg-white"
                style={{ 
                  border: '1px solid rgba(190, 147, 212, 0.15)',
                  borderRadius: '18px',
                  boxShadow: '0 4px 20px -8px rgba(75, 46, 93, 0.06)'
                }}
              >
                <AccordionTrigger 
                  className="text-left hover:no-underline"
                  style={{ color: 'var(--brand-deep-plum)' }}
                >
                  <span className="heading-3 font-semibold">
                    {faq.question}
                  </span>
                </AccordionTrigger>
                <AccordionContent 
                  className="body-regular pt-4"
                  style={{ color: 'var(--brand-charcoal)' }}
                >
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
